"use client"

/**
 * Single-field compact date-range filter chip.
 *
 * Renders as one of N independent filter chips in a table toolbar — parallel
 * to `MultiSelectFilter`. Each chip owns its own popover + its own range.
 * Multiple chips can be active simultaneously (e.g. "Date Added ∈ Apr 1–15
 * AND Hire Date ∈ Jan–Mar 2024").
 *
 * Do NOT use the old `DateRangePicker` `dateFieldOptions`/`activeDateField`
 * props — that renders pills inside ONE popover, conflating "which field"
 * with "which range" and preventing stacking. Use this wrapper instead.
 * See `docs/frontend/unified-table-contract.md` + memory entry
 * `project_date_filter_chip_spec.md`.
 */
import * as React from "react"
import type { DateRange } from "react-day-picker"
import {
  DateRangePicker,
  type DateRangePreset,
} from "@/components/ui/date-range-picker"

export interface DateFieldFilterConfig {
  /** Stable id for persistence + URL sync (e.g. 'addedAt', 'hireDate'). */
  key: string
  /** Chip label ("Date Added", "Hire Date", "Modified Date"). */
  label: string
  /** Current range value (controlled). */
  value: DateRange | undefined
  /** Called on any range change or clear. */
  onChange: (range: DateRange | undefined) => void
  /** Optional custom preset list. Falls back to DateRangePicker defaults. */
  presets?: DateRangePreset[]
  /** Block future dates in the calendar. Default false. */
  disableFutureDates?: boolean
}

interface DateFieldFilterProps extends DateFieldFilterConfig {
  className?: string
}

export function DateFieldFilter({
  label,
  value,
  onChange,
  presets,
  disableFutureDates = false,
  className,
}: DateFieldFilterProps) {
  return (
    <DateRangePicker
      placeholder={label}
      value={value}
      onChange={onChange}
      presets={presets}
      disableFutureDates={disableFutureDates}
      className={className}
    />
  )
}
